import { useEffect, useState, useRef } from "react";
import { Clock, Thermometer, MapPin, Loader2, ChevronUp } from "lucide-react";
import { ROOM_GEO_INFO, isGeographicRoom } from "../utils/roomGeoInfo";

// Cache temperature responses for 10 minutes — open-meteo is free but we don't need
// to hammer their API every time someone enters/leaves a room.
const WEATHER_CACHE = new Map(); // key: roomId, value: { tempC, forecast, fetchedAt }
const WEATHER_TTL_MS = 10 * 60 * 1000;

// WMO weather code → emoji + short label.
// https://open-meteo.com/en/docs#weather_code
const WEATHER_CODE_MAP = {
  0:  { emoji: "☀️", label: "Clear" },
  1:  { emoji: "🌤️", label: "Mostly Clear" },
  2:  { emoji: "⛅", label: "Partly Cloudy" },
  3:  { emoji: "☁️", label: "Cloudy" },
  45: { emoji: "🌫️", label: "Foggy" },
  48: { emoji: "🌫️", label: "Foggy" },
  51: { emoji: "🌦️", label: "Light Drizzle" },
  53: { emoji: "🌦️", label: "Drizzle" },
  55: { emoji: "🌦️", label: "Heavy Drizzle" },
  56: { emoji: "🌨️", label: "Freezing Drizzle" },
  57: { emoji: "🌨️", label: "Freezing Drizzle" },
  61: { emoji: "🌧️", label: "Light Rain" },
  63: { emoji: "🌧️", label: "Rain" },
  65: { emoji: "🌧️", label: "Heavy Rain" },
  66: { emoji: "🌨️", label: "Freezing Rain" },
  67: { emoji: "🌨️", label: "Freezing Rain" },
  71: { emoji: "🌨️", label: "Light Snow" },
  73: { emoji: "❄️", label: "Snow" },
  75: { emoji: "❄️", label: "Heavy Snow" },
  77: { emoji: "❄️", label: "Snow Grains" },
  80: { emoji: "🌦️", label: "Showers" },
  81: { emoji: "🌧️", label: "Showers" },
  82: { emoji: "⛈️", label: "Heavy Showers" },
  85: { emoji: "🌨️", label: "Snow Showers" },
  86: { emoji: "🌨️", label: "Heavy Snow Showers" },
  95: { emoji: "⛈️", label: "Thunderstorm" },
  96: { emoji: "⛈️", label: "Storm w/ Hail" },
  99: { emoji: "⛈️", label: "Severe Storm" },
};

const codeToWeather = (code) => WEATHER_CODE_MAP[code] || { emoji: "🌡️", label: "—" };

/**
 * Small status bar shown at the top of any geographic chat room.
 * Displays the local time (live, updating every second) and the current
 * outside temperature pulled from open-meteo.com (no API key required).
 *
 * Click the temperature pill to expand a 3-day mini forecast with weather emojis.
 *
 * Disappears silently if the room isn't in ROOM_GEO_INFO.
 */
const LocationInfoBar = ({ roomId }) => {
  const geo = ROOM_GEO_INFO[roomId];
  const [now, setNow] = useState(new Date());
  const [tempC, setTempC] = useState(null);
  const [forecast, setForecast] = useState(null); // [{ date, tmaxC, tminC, code }]
  const [tempErr, setTempErr] = useState(false);
  const [loadingTemp, setLoadingTemp] = useState(false);
  const [showForecast, setShowForecast] = useState(false);
  const lastRoomRef = useRef(null);
  const forecastRef = useRef(null);

  // Tick the clock every second
  useEffect(() => {
    if (!geo) return;
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, [geo]);

  // Fetch current temperature + 3-day forecast in a single open-meteo call
  useEffect(() => {
    if (!geo) return;
    lastRoomRef.current = roomId;
    setShowForecast(false);

    const cached = WEATHER_CACHE.get(roomId);
    if (cached && Date.now() - cached.fetchedAt < WEATHER_TTL_MS) {
      setTempC(cached.tempC);
      setForecast(cached.forecast);
      setTempErr(false);
      return;
    }

    let cancelled = false;
    setLoadingTemp(true);
    const url = `https://api.open-meteo.com/v1/forecast?latitude=${geo.lat}&longitude=${geo.lon}&current=temperature_2m&daily=weather_code,temperature_2m_max,temperature_2m_min&forecast_days=3&temperature_unit=celsius&timezone=auto`;
    fetch(url)
      .then(r => r.json())
      .then(data => {
        if (cancelled || lastRoomRef.current !== roomId) return;
        const t = data?.current?.temperature_2m;
        const daily = data?.daily;
        let fc = null;
        if (daily?.time && daily.time.length > 0) {
          fc = daily.time.map((d, i) => ({
            date: d,
            tmaxC: daily.temperature_2m_max?.[i],
            tminC: daily.temperature_2m_min?.[i],
            code: daily.weather_code?.[i],
          }));
        }
        if (typeof t === "number") {
          setTempC(t);
          setForecast(fc);
          setTempErr(false);
          WEATHER_CACHE.set(roomId, { tempC: t, forecast: fc, fetchedAt: Date.now() });
        } else {
          setTempErr(true);
        }
      })
      .catch(() => {
        if (cancelled || lastRoomRef.current !== roomId) return;
        setTempErr(true);
      })
      .finally(() => { if (!cancelled) setLoadingTemp(false); });

    return () => { cancelled = true; };
  }, [roomId, geo]);

  // Close forecast popover on outside click
  useEffect(() => {
    if (!showForecast) return;
    const handler = (e) => {
      if (forecastRef.current?.contains(e.target)) return;
      setShowForecast(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showForecast]);

  if (!geo) return null;

  const timeStr = new Intl.DateTimeFormat("en-US", {
    timeZone: geo.timezone,
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  }).format(now);

  const tempF = tempC !== null ? Math.round(tempC * 9 / 5 + 32) : null;
  const canShowForecast = !tempErr && tempC !== null && Array.isArray(forecast) && forecast.length > 0;

  const fmtDayLabel = (isoDate, idx) => {
    if (idx === 0) return "Today";
    if (idx === 1) return "Tomorrow";
    try {
      return new Date(isoDate + "T12:00:00").toLocaleDateString("en-US", { weekday: "short" });
    } catch {
      return isoDate;
    }
  };

  return (
    <div
      className="flex items-center gap-3 px-3 py-1 bg-gradient-to-r from-sky-100 via-white to-amber-100 border-2 border-sky-400 rounded mb-2 text-xs shadow-sm relative"
      data-testid="location-info-bar"
    >
      <div className="flex items-center gap-1 text-sky-700 font-bold flex-shrink-0">
        <MapPin className="w-3.5 h-3.5" />
        <span data-testid="loc-city">{geo.city}</span>
        <span className="text-sky-500 hidden sm:inline">· {geo.country}</span>
      </div>
      <div className="flex items-center gap-1 text-zinc-800 font-mono font-bold border-l border-sky-300 pl-3" data-testid="loc-time">
        <Clock className="w-3.5 h-3.5 text-sky-700" />
        {timeStr}
      </div>

      {/* Clickable temperature pill — opens forecast popover */}
      <button
        type="button"
        onClick={() => canShowForecast && setShowForecast(v => !v)}
        className={`flex items-center gap-1 font-bold border-l border-sky-300 pl-3 ml-auto rounded ${
          canShowForecast ? "text-orange-700 hover:bg-orange-50 cursor-pointer transition-colors" : "text-orange-700 cursor-default"
        }`}
        data-testid="loc-temp"
        title={canShowForecast ? "Click for 3-day forecast" : ""}
        disabled={!canShowForecast}
      >
        <Thermometer className="w-3.5 h-3.5" />
        {loadingTemp ? (
          <Loader2 className="w-3 h-3 animate-spin" />
        ) : tempErr ? (
          <span className="text-zinc-400">—</span>
        ) : tempC !== null ? (
          <>
            <span>{tempF}°F</span>
            <span className="text-orange-500 text-[10px]">/ {Math.round(tempC)}°C</span>
            {canShowForecast && (
              <ChevronUp
                className={`w-3 h-3 transition-transform ${showForecast ? "" : "rotate-180"}`}
              />
            )}
          </>
        ) : (
          <span className="text-zinc-400">—</span>
        )}
      </button>

      {/* 3-Day Forecast Popover */}
      {showForecast && canShowForecast && (
        <div
          ref={forecastRef}
          className="absolute top-full right-0 mt-1 bg-white border-2 border-sky-400 rounded shadow-2xl p-2 z-[8000] min-w-[260px]"
          data-testid="weather-forecast-popover"
        >
          <div className="text-[10px] uppercase tracking-wider text-sky-700 font-bold mb-1 flex items-center gap-1">
            <Thermometer className="w-3 h-3" />
            3-Day Forecast · {geo.city}
          </div>
          <div className="grid grid-cols-3 gap-2">
            {forecast.slice(0, 3).map((day, idx) => {
              const w = codeToWeather(day.code);
              const tmaxF = typeof day.tmaxC === "number" ? Math.round(day.tmaxC * 9 / 5 + 32) : null;
              const tminF = typeof day.tminC === "number" ? Math.round(day.tminC * 9 / 5 + 32) : null;
              return (
                <div
                  key={day.date}
                  className="bg-gradient-to-b from-sky-50 to-amber-50 border border-sky-200 rounded p-1.5 text-center"
                  data-testid={`forecast-day-${idx}`}
                >
                  <div className="text-[10px] font-bold text-sky-700">{fmtDayLabel(day.date, idx)}</div>
                  <div className="text-2xl my-0.5" title={w.label}>{w.emoji}</div>
                  <div className="text-[9px] text-zinc-500 leading-tight">{w.label}</div>
                  {tmaxF !== null && (
                    <div className="text-[11px] font-bold mt-1">
                      <span className="text-red-600">{tmaxF}°</span>
                      <span className="text-zinc-400 mx-0.5">/</span>
                      <span className="text-blue-600">{tminF}°</span>
                    </div>
                  )}
                  <div className="text-[8px] text-zinc-400">
                    {Math.round(day.tmaxC)}° / {Math.round(day.tminC)}°C
                  </div>
                </div>
              );
            })}
          </div>
          <div className="text-[9px] text-zinc-400 text-center mt-1">Data: open-meteo.com</div>
        </div>
      )}
    </div>
  );
};

export default LocationInfoBar;
export { isGeographicRoom };

